import { useState } from 'react';
import { Alert, Badge, Collapse, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconCheck, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { ScheduleConflict } from '../../lib/scheduleValidation';

type Props = {
  conflicts: ScheduleConflict[];
  onHighlightEntries?: (entryIds: string[]) => void;
};

export function AblaufplanConflictsPanel({ conflicts, onHighlightEntries }: Props) {
  const [open, setOpen] = useState(true);

  const errors = conflicts.filter((c) => c.severity === 'error');
  const warnings = conflicts.filter((c) => c.severity === 'warning');

  const color = errors.length > 0 ? 'red' : warnings.length > 0 ? 'orange' : 'green';
  const Icon = errors.length > 0 ? IconAlertCircle : warnings.length > 0 ? IconAlertTriangle : IconCheck;

  const summary =
    conflicts.length === 0
      ? 'Keine Konflikte gefunden.'
      : `${errors.length > 0 ? `${errors.length} Fehler` : ''}${errors.length > 0 && warnings.length > 0 ? ', ' : ''}${warnings.length > 0 ? `${warnings.length} Warnungen` : ''}`;

  return (
    <Alert
      color={color}
      variant="light"
      p="xs"
      radius="md"
      icon={<Icon size={16} />}
    >
      <Stack gap={4}>
        <UnstyledButton onClick={() => setOpen((v) => !v)} style={{ width: '100%' }}>
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              {summary}
            </Text>
            {conflicts.length > 0 &&
              (open ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)}
          </Group>
        </UnstyledButton>

        <Collapse in={open && conflicts.length > 0}>
          <Stack gap={4} mt={4}>
            {conflicts.map((conflict, i) => (
              <ConflictRow
                key={i}
                conflict={conflict}
                onClick={
                  onHighlightEntries
                    ? () => onHighlightEntries(conflict.entryIds)
                    : undefined
                }
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Alert>
  );
}

function ConflictRow({
  conflict,
  onClick,
}: {
  conflict: ScheduleConflict;
  onClick?: () => void;
}) {
  const color = conflict.severity === 'error' ? 'red' : 'orange';
  const label =
    conflict.type === 'participant_overlap'
      ? '⚡ Überschneidung'
      : conflict.type === 'missing_assignment'
        ? '⚠ Keine Zuordnung'
        : conflict.type === 'missing_submission'
          ? '🚫 Fehlende Anmeldung'
          : '📂 Keine Kategorie';

  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        background: 'rgba(0,0,0,0.1)',
        borderRadius: 6,
        padding: '4px 8px',
        cursor: onClick ? 'pointer' : 'default',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <Group gap="xs" wrap="nowrap">
        <Badge color={color} size="xs" variant="filled" style={{ flexShrink: 0 }}>
          {label}
        </Badge>
        <Text size="xs" style={{ flex: 1, minWidth: 0 }} lineClamp={2}>
          {conflict.message}
        </Text>
        {onClick && (
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            anzeigen →
          </Text>
        )}
      </Group>
    </UnstyledButton>
  );
}
